import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

/**
 * True when the worker is enrolled in the Limited Access experiment via the LD flag
 * AND has stage=RESTRICTED. Does NOT check current Clipboard score.
 *
 * Use this for copy / branch decisions in cancel and clock-out screens that must
 * survive a score change mid-view (e.g. ShiftCancelPageConfirmationScreen,
 * RestrictedAccountContent).
 */
export function useIsLimitedAccessWorker(): boolean {
  const { stage } = useDefinedWorker();
  const flag = useCbhFlag(CbhFeatureFlag.SHORT_LEAD_SHIFT_ACCESS, {
    defaultValue: { enabled: false },
  });

  return flag.enabled && stage === WorkerStage.RESTRICTED;
}

/**
 * True when the worker can actively browse and book Limited Access shifts:
 * enrolled in the experiment AND current Clipboard score > 0.
 *
 * Use this for active UI gates (banner visibility, ListView fall-through,
 * Callouts fall-through, ClipboardScoreBanner gate, info page route).
 *
 * While the score profile is loading, returns true if the worker is enrolled —
 * trial UI shows immediately. If the loaded score is ≤ 0, hook flips to false
 * and the worker reverts to the default RestrictedPage UI.
 */
export function useCanBookLimitedAccessShifts(): boolean {
  const isLimitedAccessWorker = useIsLimitedAccessWorker();
  const { userId } = useDefinedWorker();
  const { data: scoreProfile } = useGetClipboardScoreProfile(userId, {
    enabled: isLimitedAccessWorker,
  });

  if (!isLimitedAccessWorker) {
    return false;
  }

  if (scoreProfile === undefined) {
    return true;
  }

  return scoreProfile.clipboardScore > 0;
}
